import { API_URL, SOCKET_URL } from "../config/backend";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { io } from "socket.io-client";
import { ArrowRight, Users } from "lucide-react";
import { toast } from "sonner";
import Crewmate from "../components/Crewmate";

const BACKEND_URL = SOCKET_URL;

const CREW_COLORS = ["#ff3cac","#00f5ff","#ffe600","#3dffa0","#ff8c00","#9b5de5","#ff3355","#FF88FF"];

export default function JoinPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [roomCode, setRoomCode] = useState("");
  const [playerName, setPlayerName] = useState(() => localStorage.getItem("neonparty_player_name") || "");
  const [step, setStep] = useState(1);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState("");
  const [crewColor, setCrewColor] = useState(() => CREW_COLORS[Math.floor(Math.random() * CREW_COLORS.length)]);

  useEffect(() => {
    const roomFromUrl = searchParams.get("room");
    if (roomFromUrl && roomFromUrl.length === 4) {
      setRoomCode(roomFromUrl.toUpperCase());
      setStep(2);
    }
    // Color already randomized at init
  }, []);

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    if (roomCode.length === 4) { setStep(2); setError(""); }
    else setError("Код комнаты — 4 символа");
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!playerName.trim()) { setError("Введите своё имя"); return; }
    setJoining(true); setError("");
    const normalizedName = playerName.trim().charAt(0).toUpperCase() + playerName.trim().slice(1).toLowerCase();
    const normalizedRoom = roomCode.toUpperCase();
    const tokenKey = `playerToken:${normalizedRoom}:${normalizedName}`;
    const reconnectToken = sessionStorage.getItem(tokenKey) || "";

    // Check if name is already taken in this room. A matching reconnect token
    // means it is our own reserved slot, so let the server authenticate it.
    try {
      const API = API_URL;
      const r = await fetch(`${API}/rooms/${roomCode.toUpperCase()}`);
      const room = await r.json();
      if (room.players) {
        const taken = room.players.map(p => p.name.toLowerCase());
        if (taken.includes(normalizedName.toLowerCase()) && !reconnectToken) {
          setError("Это имя уже занято — выбери другое");
          setJoining(false);
          return;
        }
      }
    } catch {
      // если API недоступен — не блокируем, пробуем войти
    }

    const socket = io(BACKEND_URL, {
      transports: ["polling", "websocket"],
      tryAllTransports: true,
      path: "/api/socket.io",
      timeout: 12000,
      reconnection: false,
    });
    let finished = false;
    let joinTimeout = null;
    const finish = () => { finished = true; if (joinTimeout) clearTimeout(joinTimeout); };

    socket.on("connect", () => {
      socket.emit("player_join", {
        room_id: normalizedRoom,
        player_name: normalizedName,
        color: crewColor,
        player_token: reconnectToken,
      });
    });

    socket.on("player_joined", (data) => {
      finish();
      const confirmedName = data.player?.name || normalizedName;
      sessionStorage.setItem("playerName", confirmedName);
      localStorage.setItem("neonparty_player_name", confirmedName);
      sessionStorage.setItem("roomId", data.room_id);
      sessionStorage.setItem("gameType", data.game_type);
      sessionStorage.setItem("playerColor", data.player?.color || crewColor);
      if (data.player_token) {
        sessionStorage.setItem(`playerToken:${data.room_id}:${confirmedName}`, data.player_token);
      }
      socket.disconnect();
      navigate(`/play/${data.room_id}`);
    });

    socket.on("error", (data) => {
      finish();
      const message = data?.message || "Не удалось войти в комнату";
      if (message.includes("имя уже занято") && reconnectToken) {
        sessionStorage.removeItem(tokenKey); // stale token from an older room/session
      }
      setError(message); setJoining(false); socket.disconnect();
    });
    socket.on("connect_error", () => { finish(); setError("Не удалось подключиться"); setJoining(false); socket.disconnect(); });
    joinTimeout = setTimeout(() => {
      if (!finished) {
        finished = true;
        setError("Время ожидания истекло");
        setJoining(false);
        socket.disconnect();
      }
    }, 15000);
  };

  return (
    <div className="min-h-screen bg-cartoon stars-bg flex flex-col mobile-full">

      {/* Header */}
      <header className="relative z-10 p-6 text-center">
        <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-2xl"
             style={{ background: "#000", border: "2.5px solid #000", boxShadow: "4px 4px 0 #ff3cac" }}>
          <Crewmate color="#ff3cac" size={28} animate="bounce"/>
          <span className="font-display text-xl text-white tracking-wider">NEONPARTY</span>
        </div>
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">

          {step === 1 ? (
            <div className="card-cartoon animate-slide-up text-center">
              {/* Crewmate with question mark */}
              <div className="flex justify-center mb-4">
                <Crewmate color="#00f5ff" size={72} animate="bounce"/>
              </div>
              <h1 className="font-display text-3xl text-white mb-6">Код комнаты</h1>

              <input
                type="text"
                value={roomCode}
                onChange={(e) => setRoomCode(e.target.value.toUpperCase().slice(0,4))}
                placeholder="XXXX"
                maxLength={4}
                autoFocus
                data-testid="room-code-input"
                className="input-cartoon mb-4 text-4xl tracking-[0.5em] py-5"
              />

              {error && <p className="font-body text-[#ff3355] font-bold mb-4 animate-shake">{error}</p>}

              <button
                onClick={handleCodeSubmit}
                disabled={roomCode.length !== 4}
                data-testid="code-submit-btn"
                className={`btn-cartoon w-full py-4 text-xl ${roomCode.length === 4 ? "btn-secondary" : "btn-dark"}`}
              >
                Далее <ArrowRight className="w-5 h-5"/>
              </button>
            </div>

          ) : (
            <div className="card-cartoon animate-slide-up text-center">
              <button onClick={() => setStep(1)} className="text-[#aaa] text-sm mb-4 block hover:text-white transition-colors">
                ← Назад
              </button>

              {/* Crewmate in chosen color — animates when name is typed */}
              <div className="flex justify-center mb-4">
                <Crewmate color={crewColor} size={72} animate={playerName ? "bounce" : undefined}/>
              </div>

              <h1 className="font-display text-3xl text-white mb-1">Твоё имя?</h1>
              <p className="text-[#aaa] mb-5 font-heading">
                Комната: <span className="font-display text-[#ffe600]">{roomCode}</span>
              </p>

              <input
                type="text"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value.slice(0,12))}
                placeholder="Имя"
                maxLength={12}
                autoFocus
                data-testid="player-name-input"
                className="input-cartoon mb-1 text-2xl normal-case tracking-normal py-4"
              />
              <p className="text-[#555] text-xs mb-4 font-body">макс. 12 символов</p>

              {/* Color picker row */}
              <div className="flex justify-center gap-2 mb-5">
                {CREW_COLORS.map((c, i) => (
                  <button key={i} onClick={() => setCrewColor(c)} style={{
                    width: "1.6rem", height: "1.6rem", borderRadius: "50%",
                    background: c, border: crewColor === c ? "2.5px solid #fff" : "2px solid #000",
                    boxShadow: crewColor === c ? `0 0 8px ${c}` : "2px 2px 0 #000",
                    transform: crewColor === c ? "scale(1.25)" : "scale(1)",
                    transition: "all .15s", cursor: "pointer",
                  }}/>
                ))}
              </div>

              {error && <p className="font-body text-[#ff3355] font-bold mb-4 animate-shake">{error}</p>}

              <button
                onClick={handleJoin}
                disabled={joining || !playerName.trim()}
                data-testid="join-btn"
                className={`btn-cartoon w-full py-4 text-xl ${!joining && playerName.trim() ? "btn-success" : "btn-dark"}`}
              >
                {joining ? (
                  <>
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"/>
                    Подключение...
                  </>
                ) : (
                  <><Users className="w-5 h-5"/> Войти!</>
                )}
              </button>
            </div>
          )}
        </div>
      </main>

      <footer className="relative z-10 p-6 text-center text-[#444] text-sm font-body">
        Играй с друзьями! 🎉
      </footer>
    </div>
  );
}